import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/joy';
import PropTypes from 'prop-types';

import LookupTableForm from './LookupTableForm';
import EventForm from './EventForm';
import LocationForm from './LocationForm';
import PersonForm from './PersonForm';

import { lookupTableClasses } from 'utils/LookupTableUtils';
import noteActions from 'actions/noteActions';
import lookupTableActions from 'actions/lookupTableActions';

const EditModal = ({ open, onClose, className, recordId }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);
    const [locationNotes, setLocationNotes] = useState([]);

    useEffect(() => {
        if (open && recordId && className) {
            dispatch(lookupTableActions.fetchAllOptions());
            fetchRecord();
            if (className === 'Location') {
                fetchLocationNotes();
            }
        }
    }, [open, recordId, className]);

    const fetchRecord = async () => {
        setLoading(true);
        setError(null);
        try {
            const query = new Parse.Query(className);
            
            if (className === 'Event') {
                query.include(['type']);
            } else if (className === 'Location') {
                query.include(['parent']);
            }
            
            const result = await query.get(recordId);
            
            if (className === 'Person') {
                const expertiseQuery = new Parse.Query('PersonExpertise');
                expertiseQuery.equalTo('person', result);
                expertiseQuery.include(['subject']);
                const expertiseRecords = await expertiseQuery.find();
                const subjects = expertiseRecords.map(exp => exp.get('subject'));
                result.set('subjects', subjects);
            }
            
            setRecord(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocationNotes = async () => {
        try {
            const result = await dispatch(noteActions.list({ location: recordId }));
            const existingNotes = (result.value || []).map(note => ({
                id: note.id,
                type: note.get('type'),
                text: note.get('text') || '',
                isDefault: true
            }));
            setLocationNotes(existingNotes);
        } catch (err) {
            console.error('Failed to fetch location notes:', err);
        }
    };

    const handleSubmit = async (savedRecord) => {
        onClose();
    };

    const handleLocationSubmit = async (location, notes, noteDiff) => {
        try {
            const { toCreate, toUpdate, toDelete } = noteDiff;
            
            await Promise.all(toDelete.map(note => 
                dispatch(noteActions.remove(note.id))
            ));
            
            await Promise.all(toCreate.map(note => 
                dispatch(noteActions.save({
                    location: location.id,
                    type: note.type.id || note.type,
                    text: note.text,
                    parent: location.id
                }))
            ));
            
            await Promise.all(toUpdate.map(note => 
                dispatch(noteActions.update(note.id, {
                    location: location.id,
                    type: note.type.id || note.type,
                    text: note.text,
                    parent: location.id
                }))
            ));
            
            onClose();
        } catch (err) {
            console.error('Failed to update location notes:', err);
            onClose();
        }
    };

    const getFormComponent = () => {
        if (loading) {
            return <CircularProgress />;
        }

        if (error) {
            return <div style={{ color: 'red' }}>Error loading record: {error}</div>;
        }

        const isLookupTable = lookupTableClasses.some(ltc => ltc.className === className);
        
        const formProps = {
            onSubmit: handleSubmit,
            initialValues: record
        };

        if (isLookupTable || className === 'Subject') {
            return <LookupTableForm {...formProps} className={className} />;
        }

        switch (className) {
            case 'Event':
                return <EventForm {...formProps} />;
            case 'Location':
                return <LocationForm 
                    {...formProps} 
                    notes={locationNotes}
                    onSubmit={handleLocationSubmit}
                />;
            case 'Person':
                return <PersonForm {...formProps} />;
            default:
                return <div>No form available for {className}</div>;
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog variant="outlined" size="lg" sx={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
                <DialogTitle>Edit {className}</DialogTitle>
                <DialogContent>
                    {getFormComponent()}
                </DialogContent>
                {!loading && !error && (
                    <DialogActions>
                        <Button variant="plain" color="neutral" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogActions>
                )}
            </ModalDialog>
        </Modal>
    );
};

EditModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    className: PropTypes.string,
    recordId: PropTypes.string
};

export default EditModal;